import React, { useCallback, useEffect, useMemo } from 'react';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { SlideMenu } from 'apps/sigma2-ui/src/components/SlideMenu';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useGlobalFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useGlobalFilters';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { getFiltersFromSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { OnTryInitializeSecondaryFilterFn } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { OnApplyGlobalFn } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FilterDefinitionForExtrasArray } from 'libs/end-to-end/src/types/databases/filters/filter-definition';

export const DashboardAddGlobalFiltersSlideMenu = ({
  isOpen,
  setIsOpen,
  onSaveAndClose,
}: {
  isOpen: boolean;
  setIsOpen: Setter<boolean>;
  onSaveAndClose: (
    newGlobalFilters: FilterDefinitionForExtrasArray | null,
  ) => void;
}) => {
  const language = useLanguage();
  const { updateDashboardDraftGlobalFilters, dashboardDraft } = useDashboards();

  const dashboardId = useMemo(() => {
    return dashboardDraft?.dashboardId || '';
  }, [dashboardDraft?.dashboardId]);

  const onTryInitializeGlobalFilterCb: OnTryInitializeSecondaryFilterFn =
    useCallback(
      (params) => {
        if (!dashboardDraft) {
          return null;
        }

        const globalFilter = dashboardDraft.draft.globalFilters?.find(
          (f) => f.columnHeaderValue === params.secondaryFilterKey,
        );

        if (!globalFilter) {
          return params.defaultValue;
        }

        return globalFilter.values;
      },
      [dashboardDraft],
    );

  const globalFiltersHook = useGlobalFilters({
    scopeId: `slide-menu/${dashboardId || ''}`,
    disabled: false,
    onTryInitializeGlobalFilter: onTryInitializeGlobalFilterCb,
  });

  // When the modal is opened, let's initialize the state of the global filters hook to match the current draft global filters.
  const maybeReinitializeFromDraftGlobalFilters = useCallback(() => {
    if (!globalFiltersHook.isInitialized) {
      return;
    }

    const currentSelections = globalFiltersHook.globalFilterSelections;

    const newValue = currentSelections.map((current) => {
      const draftSelection = dashboardDraft?.draft.globalFilters?.find(
        (g) => g.columnHeaderValue === current.secondaryFilterKey,
      );

      current.secondaryFilterKey;
      return {
        secondaryFilterKey: current.secondaryFilterKey,
        value: draftSelection ? draftSelection.values : [],
      };
    });

    console.log(
      '[1] attempting to init global filters with',
      newValue,
      globalFiltersHook.globalFilterSelections,
    );

    globalFiltersHook.setFilters({
      globalFilterSelections: newValue,
      shouldPreventAutoSubmit: true,
    });
  }, [dashboardDraft?.draft.globalFilters, globalFiltersHook]);

  // TODO: Is there a way to avoid running this on every open?
  useEffect(() => {
    if (isOpen) {
      maybeReinitializeFromDraftGlobalFilters();
    }
  }, [isOpen]);

  const handleSave = useCallback(() => {
    const newSelection = getFiltersFromSecondaryFilterSelections({
      secondaryFilterSelections: globalFiltersHook.globalFilterSelections,
    }).filter((f) => f.values.length > 0);
    console.log('New selection to save as global filters:', newSelection);
    if (newSelection.length === 0) {
      updateDashboardDraftGlobalFilters(null);
    } else {
      updateDashboardDraftGlobalFilters(newSelection);
    }
    setIsOpen(false);
    onSaveAndClose(newSelection.length === 0 ? null : newSelection);
  }, [
    globalFiltersHook.globalFilterSelections,
    setIsOpen,
    updateDashboardDraftGlobalFilters,
    onSaveAndClose,
  ]);

  return (
    <SlideMenu
      show={isOpen}
      onClose={() => setIsOpen(false)}
      width="w-1/3"
      qaDataName={'dashboard-add-global-filters-slide-menu'}
    >
      <div className="-m-6">
        <Spacing
          direction="horizontal"
          size="lg"
          className="px-8 py-4 bg-gray-50 border-gray-200 border-b-1 border-solid items-center justify-between"
        >
          <S2Typography
            translatedContent={language.dashboardAddGlobalFiltersSlideHeading()}
            variant={'headline-5'}
            textColorVariant={'gray-900'}
          />
          <PrimaryButtonLightBg
            onClick={handleSave}
            disabled={false}
            translatedContent={language.save()}
            innerClassName="py-1 !px-4"
          />
        </Spacing>
        <div className="px-8 py-6">
          <S2Typography
            translatedContent={language.dashboardAddGlobalFiltersSlideDescription()}
            variant={'body-6'}
            textColorVariant={'gray-900'}
          />
        </div>
        <div className="px-8 pt-6">
          <S2Typography
            variant="body-6"
            translatedContent={language.filters()}
          />

          <HorizontalFilterBar items={globalFiltersHook.items} />
        </div>
      </div>
    </SlideMenu>
  );
};
